import type { ConsentLinkStateConsentsType, paths } from '../../types/clients/marketing-automation'
import { PathsApiV1PublicUnsubscribeTokenPatchRequestBodyContentApplicationJsonType as ConsentLinkUpdateType } from '../../types/clients/marketing-automation'
import type { UseMutationMutateOptions } from '../../types'
import { useMutation, useOpenapi, useQuery } from '#imports'

type ConsentLinkState = paths['/api/v1/public/unsubscribe/{token}']['get']['responses']['200']['content']['application/json']

export interface ConsentLinkConsent {
    type: ConsentLinkStateConsentsType
    enabled: boolean
}

/**
 * @title useMarketingAutomationClient
 * @see Docs https://marketing-automation-stage.spilnu.dk/api#/
 */
export const useMarketingAutomationClient = () => {
    const client = useOpenapi<paths>({
        baseUrl: '/gateway/marketing-automation',
        service: 'marketingAutomation'
    })

    function useGetConsentLink(token: string) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/api/v1/public/unsubscribe/{token}', {
                signal,
                params: { path: { token } },
            })

            return data
        }, {
            key: 'marketingAutomation:consentLink',
            enabled: Boolean(token),
            // MA rate-limits per IP; an SSR request would come from the Nuxt server's IP
            server: false,
        })
    }

    function useUpdateConsentLink(token: string, options?: UseMutationMutateOptions<ConsentLinkState | undefined, unknown, ConsentLinkConsent[]>) {
        return useMutation(async (consents: ConsentLinkConsent[]) => {
            let data: ConsentLinkState | undefined
            for (const { type, enabled } of consents) {
                const result = await client.PATCH('/api/v1/public/unsubscribe/{token}', {
                    params: { path: { token } },
                    body: { type: ConsentLinkUpdateType[type], enabled },
                })
                data = result.data
            }
            return data
        }, options)
    }

    function useUnsubscribeAllConsentLink(token: string, options?: UseMutationMutateOptions<void, unknown, void>) {
        return useMutation(async () => {
            await client.POST('/api/v1/public/unsubscribe/{token}/all', {
                params: { path: { token } },
            })
        }, options)
    }

    return {
        client,
        useGetConsentLink,
        useUpdateConsentLink,
        useUnsubscribeAllConsentLink,
    }
}
